import axios, { AxiosResponse } from 'axios'
import toast from 'react-hot-toast'

// In dev, API calls are same-origin (`/api/...`) and the Vite dev server
// proxies them to the local API (see vite.config.ts) — this keeps phones on
// the LAN working without baking the host machine's IP into the bundle.
// Prod builds use the configured absolute API URL (VITE_API_URL build arg).
export const API_BASE = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL || ''

const API_BASE_URL = `${API_BASE}/api`

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      window.location.href = '/login'
      toast.error('Session expired. Please login again.')
    } else if (error.response?.status === 404) {
      // Check if this 404 is for an expected unimplemented endpoint
      const requestUrl = error.config?.url || ''
      const unimplementedEndpoints = [
        '/provider-dashboard/assets',
        '/provider-dashboard/bookings',
        '/provider-dashboard/earnings'
      ]

      const isUnimplementedEndpoint = unimplementedEndpoints.some(endpoint =>
        requestUrl.includes(endpoint)
      )

      // Only show toast for unexpected 404 errors
      if (!isUnimplementedEndpoint) {
        toast.error('Page not found')
      }
      // For unimplemented endpoints, let the component handle the error locally
    } else if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.')
    } else if (error.response?.data?.message) {
      toast.error(error.response.data.message)
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout. Please check your connection.')
    } else {
      toast.error('An error occurred. Please try again.')
    }
    return Promise.reject(error)
  }
)

// Generic API methods
export const apiRequest = {
  get: <T>(url: string, params?: any): Promise<AxiosResponse<T>> => {
    return api.get(url, { params })
  },

  post: <T>(url: string, data?: any): Promise<AxiosResponse<T>> => {
    return api.post(url, data)
  },

  put: <T>(url: string, data?: any): Promise<AxiosResponse<T>> => {
    return api.put(url, data)
  },

  patch: <T>(url: string, data?: any): Promise<AxiosResponse<T>> => {
    return api.patch(url, data)
  },

  delete: <T>(url: string): Promise<AxiosResponse<T>> => {
    return api.delete(url)
  },

  // File upload
  upload: <T>(url: string, formData: FormData): Promise<AxiosResponse<T>> => {
    return api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
}

// Export the upload method separately for convenience
export const apiUpload = apiRequest.upload

export default api